import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGetHuntQueryOptions,
  getListEventTwinItemsQueryKey,
  getListEventTwinItemsQueryOptions,
  type EventTwinItem,
  type EventTwinItemList,
  type PublicChallenge,
  useGetEventTwin,
  useSupportEventTwinItem,
} from '@cdf/api-client-react';
import {
  ArrowLeftIcon,
  BoxIcon,
  AlertCircleIcon,
  XIcon,
  MapIcon,
  CameraIcon,
  MapPinIcon,
  QrCodeIcon,
  SparklesIcon,
  UserIcon,
  ClockIcon,
  ChevronDownIcon,
  Minimize2Icon,
  HeartIcon,
} from 'lucide-react';
import {
  FLOOR_BOUNDS,
  ROOMS,
  BOOTHS,
  BOOTH_COLORS,
  CONFERENCE_NAV_GRAPH,
  ROOM_SEATING_GRIDS,
  type BoothDef,
  type PresentationRoomId,
  type RoomDef,
  type VirtualScreenContent,
} from '@/components/twin/conference-twin-data';
import { parseExhibitorName, parseStandNumber } from '@/components/hunt/algim-program';
import {
  PedestrianSimulation,
  createPedestrian,
  ALL_PEDESTRIAN_ROLES,
  type PedestrianState,
  type PedestrianMeta,
  type PedestrianRole,
  type PedestrianTimelineState,
  type WalkBounds,
} from '@/components/twin/pedestrian-simulation';
import { PedestrianControls } from '@/components/twin/pedestrian-controls';
import { DesktopTwinPanel } from '@/components/twin/desktop-twin-panel';
import { TwinAvatarCustomizer } from '@/components/twin/twin-avatar-customizer';
import { useLivePresencePedestrians } from '@/components/twin/use-live-presence-pedestrians';
import { useTwinPresence } from '@/hooks/use-twin-presence';
import { useFullscreenTwin } from '@/hooks/use-fullscreen-twin';
import { ScheduleTimelineSlider } from '@/components/hunt/schedule-timeline-slider';
import {
  SessionQuizCard,
  isSessionQuizChallenge,
} from '@/components/hunt/session-quiz-card';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import {
  CONFERENCE_DAY_START_MINUTES,
  formatScheduleTime12,
  parseScheduleMinutes,
  useTimeBasedHunt,
  type RoomScheduleStatus,
  type ScheduleStatus,
} from '@/hooks/use-time-based-hunt';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ConferenceTwinScene,
  isWebGLAvailable,
  ConferenceTwinLegend,
  CAMERA_PRESETS,
  type ConferenceSelectedItem,
  type ConferenceTwinCameraController,
  type HuntRoomBeacon,
} from '@/components/twin/conference-twin-scene';
import { ZoomPanControls } from '@/components/ui/zoom-pan-controls';

const CONFERENCE_WALK_BOUNDS: WalkBounds = {
  minX: FLOOR_BOUNDS.minX + 2,
  maxX: FLOOR_BOUNDS.maxX - 2,
  minZ: FLOOR_BOUNDS.minZ + 2,
  maxZ: FLOOR_BOUNDS.maxZ - 2,
};

const DEFAULT_EVENT_SLUG = 'novotel-collins-conference';
const SESSION_ARRIVAL_LEAD_MINUTES = 15;
// Conference floor origin: center of the venue's ground plane.
const CONFERENCE_GEO_ANCHOR = { latitude: -37.8158, longitude: 144.9664 } as const;
const METRES_PER_FLOOR_UNIT = 1;
const MOBILE_VIEWPORT_QUERY = '(max-width: 639px), (max-width: 935px) and (orientation: landscape), (max-height: 500px)';

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
}

/** Maps a GPS fix around the venue anchor onto the conference floor in metres. */
function projectGpsToConferenceFloor(latitude: number, longitude: number): { floorX: number; floorZ: number } {
  const latitudeRadians = CONFERENCE_GEO_ANCHOR.latitude * (Math.PI / 180);
  const metresEast = (longitude - CONFERENCE_GEO_ANCHOR.longitude) * 111_320 * Math.cos(latitudeRadians);
  const metresNorth = (latitude - CONFERENCE_GEO_ANCHOR.latitude) * 110_540;
  const centerX = (CONFERENCE_WALK_BOUNDS.minX + CONFERENCE_WALK_BOUNDS.maxX) / 2;
  const centerZ = (CONFERENCE_WALK_BOUNDS.minZ + CONFERENCE_WALK_BOUNDS.maxZ) / 2;

  return {
    floorX: Math.max(CONFERENCE_WALK_BOUNDS.minX, Math.min(CONFERENCE_WALK_BOUNDS.maxX, centerX + metresEast / METRES_PER_FLOOR_UNIT)),
    floorZ: Math.max(CONFERENCE_WALK_BOUNDS.minZ, Math.min(CONFERENCE_WALK_BOUNDS.maxZ, centerZ - metresNorth / METRES_PER_FLOOR_UNIT)),
  };
}

const CHALLENGE_ICONS: Record<string, typeof MapIcon> = {
  quiz: MapIcon,
  demo_report: MapPinIcon,
  ar_view: CameraIcon,
  cv_scan: QrCodeIcon,
  map_find: MapIcon,
};

const SPONSORS_SPACE_ANCHOR = {
  id: 'sponsors-space',
  name: 'Sponsors Space',
  shortName: 'Sponsors',
  x: -2,
  z: 0,
  height: 3.2,
} as const;

function normalizeRoomKey(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function resolveRoomGeometry(roomName: string): {
  roomId: string;
  x: number;
  z: number;
  height: number;
  shortName: string;
  displayName: string;
} | null {
  const key = normalizeRoomKey(roomName);
  if (!key) return null;

  if (key.includes('sponsor')) {
    return {
      roomId: SPONSORS_SPACE_ANCHOR.id,
      x: SPONSORS_SPACE_ANCHOR.x,
      z: SPONSORS_SPACE_ANCHOR.z,
      height: SPONSORS_SPACE_ANCHOR.height,
      shortName: SPONSORS_SPACE_ANCHOR.shortName,
      displayName: SPONSORS_SPACE_ANCHOR.name,
    };
  }

  let best: RoomDef | null = null;
  for (const room of ROOMS) {
    const candidates = [
      normalizeRoomKey(room.name),
      normalizeRoomKey(room.shortName),
      normalizeRoomKey(room.id.replace(/-/g, ' ')),
    ];
    if (candidates.some((c) => c === key || c.includes(key) || key.includes(c))) {
      best = room;
      break;
    }
  }
  if (!best) return null;
  return {
    roomId: best.id,
    x: best.x,
    z: best.z,
    height: best.height,
    shortName: best.shortName,
    displayName: best.name,
  };
}

function buildPedestrianTimeline(
  rooms: RoomScheduleStatus[],
  currentMinutes: number,
): PedestrianTimelineState {
  const targets = new Map<PresentationRoomId, 'preparing' | 'live'>();

  for (const room of rooms) {
    const geometry = resolveRoomGeometry(room.room);
    if (!geometry || !Object.hasOwn(ROOM_SEATING_GRIDS, geometry.roomId)) continue;
    const roomId = geometry.roomId as PresentationRoomId;

    if (room.status === 'live') {
      targets.set(roomId, 'live');
      continue;
    }
    if (room.status !== 'upcoming') continue;

    const nextStart = Math.min(
      ...[...room.stations, ...room.challenges]
        .filter((item) => item.status === 'upcoming' && item.startMinutes != null)
        .map((item) => item.startMinutes as number),
    );
    if (Number.isFinite(nextStart) && nextStart - currentMinutes <= SESSION_ARRIVAL_LEAD_MINUTES) {
      targets.set(roomId, 'preparing');
    }
  }

  return {
    currentMinutes,
    roomTargets: Array.from(targets, ([roomId, state]) => ({ roomId, state })),
  };
}

function pickPrimarySession(room: RoomScheduleStatus): {
  sessionTitle: string;
  presenter: string | null;
  startTime: string | null;
  endTime: string | null;
  stationId?: string;
  stationName: string;
  stationPoints: number;
  challenges: PublicChallenge[];
} {
  const matchingChallenges = room.challenges.filter((c) => c.status === room.status);
  const matchingStations = room.stations.filter((s) => s.status === room.status);
  const challengePool = matchingChallenges.length > 0 ? matchingChallenges : room.challenges;
  const stationPool = matchingStations.length > 0 ? matchingStations : room.stations;

  const primaryChallenge = challengePool[0];
  const primaryStation = stationPool[0];

  const presenter =
    primaryChallenge?.presenter ??
    primaryStation?.presenter ??
    challengePool.find((c) => c.presenter)?.presenter ??
    stationPool.find((s) => s.presenter)?.presenter ??
    null;

  const startTime =
    primaryChallenge?.startTime ??
    primaryStation?.startTime ??
    challengePool.find((c) => c.startTime)?.startTime ??
    stationPool.find((s) => s.startTime)?.startTime ??
    null;

  const endTime =
    primaryChallenge?.endTime ??
    primaryStation?.endTime ??
    challengePool.find((c) => c.endTime)?.endTime ??
    stationPool.find((s) => s.endTime)?.endTime ??
    null;

  return {
    sessionTitle:
      primaryChallenge?.title ?? primaryStation?.name ?? `${room.room} session`,
    presenter,
    startTime: startTime ?? null,
    endTime: endTime ?? null,
    stationId: primaryStation?.id ?? primaryChallenge?.stationId ?? undefined,
    stationName: primaryStation?.name ?? room.room,
    stationPoints: primaryStation?.points ?? 0,
    challenges: challengePool.length > 0 ? challengePool : room.challenges,
  };
}

function buildVirtualScreenContent(
  rooms: RoomScheduleStatus[],
): Record<string, VirtualScreenContent> {
  const contentByRoom = Object.fromEntries(
    ROOMS.map((room) => [
      room.id,
      {
        roomId: room.id,
        sessionTitle: 'Room Available',
        status: 'idle',
      } satisfies VirtualScreenContent,
    ]),
  ) as Record<string, VirtualScreenContent>;

  for (const room of rooms) {
    const geometry = resolveRoomGeometry(room.room);
    if (!geometry || !Object.hasOwn(contentByRoom, geometry.roomId)) continue;

    if (room.status !== 'live' && room.status !== 'upcoming') continue;

    const primary = pickPrimarySession(room);
    contentByRoom[geometry.roomId] = {
      roomId: geometry.roomId,
      sessionTitle: primary.sessionTitle,
      presenter: primary.presenter,
      startTime: primary.startTime,
      endTime: primary.endTime,
      status: room.status,
    };
  }

  return contentByRoom;
}

function buildHuntBeacons(
  rooms: RoomScheduleStatus[],
  completedChallengeIds: ReadonlySet<string>,
): HuntRoomBeacon[] {
  const beacons: HuntRoomBeacon[] = [];

  for (const room of rooms) {
    if (room.status !== 'live' && room.status !== 'upcoming') continue;
    const geo = resolveRoomGeometry(room.room);
    if (!geo) continue;

    const primary = pickPrimarySession(room);
    const quizIds = primary.challenges
      .filter((c) => isSessionQuizChallenge(c))
      .map((c) => c.id);
    const completedCount = quizIds.filter((id) => completedChallengeIds.has(id)).length;
    const completed = quizIds.length > 0 && completedCount === quizIds.length;

    beacons.push({
      id: `beacon-${geo.roomId}`,
      roomId: geo.roomId,
      x: geo.x,
      y: geo.height + 1.6,
      z: geo.z,
      status: room.status,
      roomLabel: geo.displayName,
      shortLabel: geo.shortName,
      stationId: primary.stationId,
      stationName: primary.stationName,
      stationPoints: primary.stationPoints,
      presenter: primary.presenter,
      startTime: primary.startTime,
      endTime: primary.endTime,
      sessionTitle: primary.sessionTitle,
      challenges: primary.challenges,
      liveCount: room.liveCount || (room.status === 'live' ? 1 : 0),
      completed,
      completedCount,
    });
  }

  return beacons;
}

function scheduleStatusBadgeVariant(
  status: ScheduleStatus | undefined,
): 'default' | 'secondary' | 'outline' {
  if (status === 'live') return 'default';
  if (status === 'upcoming') return 'secondary';
  return 'outline';
}

function formatSlotLabel(startTime?: string | null, endTime?: string | null): string | null {
  const start = parseScheduleMinutes(startTime);
  const end = parseScheduleMinutes(endTime);
  if (start == null && end == null) return null;
  if (start != null && end != null) {
    return `${formatScheduleTime12(start)} – ${formatScheduleTime12(end)}`;
  }
  if (start != null) return `From ${formatScheduleTime12(start)}`;
  return `Until ${formatScheduleTime12(end!)}`;
}

export function ConferenceTwinPage() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const huntSlug = eventSlug ?? DEFAULT_EVENT_SLUG;
  const eventTwinQuery = useGetEventTwin(huntSlug);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ConferenceSelectedItem | null>(null);
  const [selectedWallItem, setSelectedWallItem] = useState<EventTwinItem | null>(null);
  const [presetId, setPresetId] = useState('full');
  const [timelineMinutes, setTimelineMinutes] = useState(CONFERENCE_DAY_START_MINUTES);
  const [huntOverlayEnabled, setHuntOverlayEnabled] = useState(true);
  const [eventReportsOverlayEnabled, setEventReportsOverlayEnabled] = useState(true);
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isTimelineMinimized, setIsTimelineMinimized] = useState(isMobileViewport);
  const webglOk = useMemo(() => isWebGLAvailable(), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
      if (mediaQuery.matches) setIsTimelineMinimized(true);
    };
    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  const [pedEnabled, setPedEnabled] = useState(true);
  const [pedSpeed, setPedSpeed] = useState(1);
  const [timelineSync, setTimelineSync] = useState(true);
  const [showPaths, setShowPaths] = useState(false);
  const [visiblePedestrianRoles, setVisiblePedestrianRoles] = useState<PedestrianRole[]>(
    () => [...ALL_PEDESTRIAN_ROLES],
  );
  const [pedCount, setPedCount] = useState(0);
  const [selectedPed, setSelectedPed] = useState<PedestrianMeta | null>(null);
  const pedestriansRef = useRef<PedestrianState[]>([]);
  const cameraRef = useRef<ConferenceTwinCameraController>(null);
  const {
    containerRef: fullscreenContainerRef,
    isFallbackFullscreen,
    isFullscreen,
    isLandscape,
    toggleFullscreen,
    exitFullscreen,
  } = useFullscreenTwin();
  const isFullscreenLandscape = isFullscreen && isLandscape;
  const presenceSceneId = `conference:${huntSlug}`;
  const presence = useTwinPresence({
    sceneId: presenceSceneId,
    projectLocation: (location) => ({
      ...projectGpsToConferenceFloor(location.latitude, location.longitude),
      accuracyMeters: location.accuracyMeters,
    }),
  });

  useEffect(() => {
    if (selectedPed && !visiblePedestrianRoles.includes(selectedPed.role)) {
      setSelectedPed(null);
    }
  }, [selectedPed, visiblePedestrianRoles]);

  const projectConferencePresence = useCallback((participant: { latitude?: number | null; longitude?: number | null; floorX?: number | null; floorZ?: number | null }) => {
    if (participant.floorX != null && participant.floorZ != null) return new THREE.Vector3(participant.floorX, 0, participant.floorZ);
    if (participant.latitude != null && participant.longitude != null) {
      const point = projectGpsToConferenceFloor(participant.latitude, participant.longitude);
      return new THREE.Vector3(point.floorX, 0, point.floorZ);
    }
    return null;
  }, []);
  const ownLivePresence = useMemo(() => {
    if (!presence.isSharing || !presence.locationTrack.location) return null;
    const location = presence.locationTrack.location;
    const point = projectGpsToConferenceFloor(location.latitude, location.longitude);
    return {
      userId: `local:${presenceSceneId}`,
      displayName: presence.displayName,
      avatarColor: presence.avatarColor,
      accuracyMeters: location.accuracyMeters,
      position: new THREE.Vector3(point.floorX, 0, point.floorZ),
    };
  }, [presence.avatarColor, presence.displayName, presence.isSharing, presence.locationTrack.location, presenceSceneId]);
  const livePedestrians = useLivePresencePedestrians(presence.participants, projectConferencePresence, ownLivePresence);

  const handleSpawnPedestrian = useCallback(() => {
    const ped = createPedestrian(CONFERENCE_WALK_BOUNDS, { navGraph: CONFERENCE_NAV_GRAPH });
    pedestriansRef.current = [...pedestriansRef.current, ped];
    setPedCount(pedestriansRef.current.length);
  }, []);

  const handleResetPedestrians = useCallback(() => {
    pedestriansRef.current = [];
    setPedCount(0);
    setSelectedPed(null);
  }, []);

  const handleSelectPedestrian = useCallback((meta: PedestrianMeta) => {
    setSelectedPed(meta);
  }, []);

  const { data, isLoading } = useQuery({
    ...getGetHuntQueryOptions(huntSlug),
  });
  const twinItemsQuery = useQuery({
    ...getListEventTwinItemsQueryOptions(huntSlug, { limit: 100, offset: 0 }),
    refetchInterval: 10_000,
  });
  const supportMutation = useSupportEventTwinItem({
    mutation: {
      onSuccess: (result) => {
        setSelectedWallItem((current) => current?.id === result.itemId
          ? { ...current, supportCount: result.supportCount }
          : current);
        queryClient.setQueryData<EventTwinItemList>(
          getListEventTwinItemsQueryKey(huntSlug, { limit: 100, offset: 0 }),
          (current) => current && {
            ...current,
            items: current.items.map((item) =>
              item.id === result.itemId ? { ...item, supportCount: result.supportCount } : item,
            ),
          },
        );
        void queryClient.invalidateQueries({
          queryKey: getListEventTwinItemsQueryKey(huntSlug, { limit: 100, offset: 0 }),
        });
      },
    },
  });

  const { progress: huntProgress } = useHuntChallenges();
  const completedChallengeIds = useMemo(
    () => new Set<string>(huntProgress?.completedChallengeIds ?? []),
    [huntProgress?.completedChallengeIds],
  );

  const schedule = useTimeBasedHunt({
    stations: data?.stations ?? [],
    challenges: data?.challenges ?? [],
    selectedTime: timelineMinutes,
  });

  const completedSessionMarkers = useMemo(() => {
    const markers = new Set<number>();
    for (const ch of data?.challenges ?? []) {
      if (!completedChallengeIds.has(ch.id) || !isSessionQuizChallenge(ch)) continue;
      const start = parseScheduleMinutes(ch.startTime);
      if (start != null) markers.add(start);
    }
    return Array.from(markers).sort((a, b) => a - b);
  }, [completedChallengeIds, data?.challenges]);

  const completedSessionRooms = useMemo(() => {
    const rooms = new Set<string>();
    for (const ch of data?.challenges ?? []) {
      if (!completedChallengeIds.has(ch.id) || !isSessionQuizChallenge(ch)) continue;
      if (ch.room?.trim()) rooms.add(ch.room.trim());
    }
    return Array.from(rooms).sort((a, b) => a.localeCompare(b));
  }, [completedChallengeIds, data?.challenges]);

  const huntBeacons = useMemo(
    () =>
      huntOverlayEnabled
        ? buildHuntBeacons(schedule.rooms, completedChallengeIds)
        : [],
    [huntOverlayEnabled, schedule.rooms, completedChallengeIds],
  );

  const pedestrianTimeline = useMemo(
    () => buildPedestrianTimeline(schedule.rooms, timelineMinutes),
    [schedule.rooms, timelineMinutes],
  );

  const virtualScreenContent = useMemo(
    () => buildVirtualScreenContent(schedule.rooms),
    [schedule.rooms],
  );

  const wallItems = useMemo(() => (
    (twinItemsQuery.data?.items ?? [])
      .filter((item) => item.type !== 'event_report' || eventReportsOverlayEnabled)
      .map((item) => {
        if (item.type !== 'event_report' || item.latitude == null || item.longitude == null) return item;
        const position = projectGpsToConferenceFloor(item.latitude, item.longitude);
        return {
          ...item,
          wallAnchor: {
            ...item.wallAnchor,
            roomId: `gps-report:${item.sourceId}`,
            x: position.floorX,
            y: 1.8,
            z: position.floorZ,
            rotationY: 0,
          },
        };
      })
  ), [eventReportsOverlayEnabled, twinItemsQuery.data?.items]);

  const enrichSelection = useCallback(
    (item: ConferenceSelectedItem | null): ConferenceSelectedItem | null => {
      if (!item) return null;
      if (item.scheduleStatus && item.sessionTitle) return item;

      const roomName = item.roomLabel ?? item.stationName;
      const matched =
        (item.roomId
          ? schedule.rooms.find((r) => resolveRoomGeometry(r.room)?.roomId === item.roomId)
          : undefined) ??
        schedule.rooms.find((r) => {
          const geo = resolveRoomGeometry(r.room);
          return (
            normalizeRoomKey(r.room) === normalizeRoomKey(roomName) ||
            geo?.displayName === item.stationName ||
            geo?.roomId === item.roomId
          );
        });

      if (!matched || (matched.status !== 'live' && matched.status !== 'upcoming')) {
        return item;
      }

      const primary = pickPrimarySession(matched);
      return {
        ...item,
        roomId: item.roomId ?? resolveRoomGeometry(matched.room)?.roomId,
        roomLabel: matched.room,
        presenter: primary.presenter,
        startTime: primary.startTime,
        endTime: primary.endTime,
        scheduleStatus: matched.status,
        sessionTitle: primary.sessionTitle,
        stationId: item.stationId ?? primary.stationId,
        stationPoints: item.stationPoints || primary.stationPoints,
        challenges: item.challenges.length > 0 ? item.challenges : primary.challenges,
      };
    },
    [schedule.rooms],
  );

  const handleSelect = useCallback(
    (item: ConferenceSelectedItem | null) => {
      setSelected(enrichSelection(item));
      setSelectedWallItem(null);
      if (item) setSelectedPed(null);
    },
    [enrichSelection],
  );

  const handleWallItemSelect = useCallback((item: EventTwinItem) => {
    setSelected(null);
    setSelectedPed(null);
    setSelectedWallItem(item);
  }, []);

  const currentPreset = CAMERA_PRESETS.find((p) => p.id === presetId) ?? CAMERA_PRESETS[0]!;

  const selectedKey =
    selected?.roomId ??
    selected?.stationId ??
    (selected?.kind === 'booth' && selected.stand != null
      ? `stand-${selected.stand}`
      : null);

  if (isLoading || eventTwinQuery.isLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col">
        <div className="border-b border-border px-4 py-2 bg-background/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeftIcon className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-sm font-semibold truncate">
              ALGIM Learning Exchange 2026 — 3D Conference Twin
            </h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-full w-full" />
        </div>
      </div>
    );
  }

  if (eventTwinQuery.isError || !eventTwinQuery.data) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5 text-destructive" />
              Unable to load conference twin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {eventTwinQuery.error instanceof Error
                ? eventTwinQuery.error.message
                : 'The conference twin could not be loaded.'}
            </p>
            <Button asChild variant="outline">
              <Link to="/twin">
                <MapIcon className="mr-2 h-4 w-4" /> Back to Digital Twin Hub
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!webglOk) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5 text-amber-500" />
              3D not available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Your browser doesn&apos;t support WebGL, which is required for the 3D conference twin.
            </p>
            <Button asChild>
              <Link to="/twin">
                <MapIcon className="mr-2 h-4 w-4" /> Digital Twin Hub
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className={isFullscreenLandscape ? 'hidden' : 'z-20 flex flex-wrap items-center gap-3 border-b border-border bg-background/95 px-4 py-2 backdrop-blur'}>
        <Button asChild variant="ghost" size="sm">
          <Link to="/twin">
            <ArrowLeftIcon className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <BoxIcon className="h-4 w-4 text-indigo-500" />
          <h1 className="text-sm font-semibold truncate">{eventTwinQuery.data.twin.title} — 3D Conference Twin</h1>
        </div>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {data?.stations.length ?? 0} stations · {data?.challenges.length ?? 0} challenges
        </span>

        <div className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-2.5 py-1">
          <SparklesIcon className="h-3.5 w-3.5 text-amber-500" aria-hidden />
          <Label
            htmlFor="hunt-overlay-toggle"
            className="cursor-pointer text-xs font-medium whitespace-nowrap"
          >
            Treasure Hunt Overlay
          </Label>
          <Switch
            id="hunt-overlay-toggle"
            checked={huntOverlayEnabled}
            onCheckedChange={setHuntOverlayEnabled}
            aria-label="Toggle treasure hunt overlay"
          />
          {huntOverlayEnabled && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              {huntBeacons.filter((b) => b.status === 'live').length} live
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-2.5 py-1">
          <Label
            htmlFor="event-reports-overlay-toggle"
            className="cursor-pointer text-xs font-medium whitespace-nowrap"
          >
            Event reports
          </Label>
          <Switch
            id="event-reports-overlay-toggle"
            checked={eventReportsOverlayEnabled}
            onCheckedChange={setEventReportsOverlayEnabled}
            aria-label="Toggle event report pins"
          />
        </div>

        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <TwinAvatarCustomizer sceneLabel={eventTwinQuery.data.twin.title} presence={presence} />
          {CAMERA_PRESETS.map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant={presetId === p.id ? 'default' : 'outline'}
              className="h-7 text-xs px-2.5"
              onClick={() => setPresetId(p.id)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div
        ref={fullscreenContainerRef}
        className={
          isFallbackFullscreen
            ? 'fixed inset-0 z-40 h-[100dvh] w-screen bg-background'
            : isFullscreen
              ? 'relative h-full w-full bg-background'
              : 'relative flex-1'
        }
      >
        {isFullscreenLandscape && (
          <div className="absolute inset-x-0 top-0 z-50 flex min-h-14 items-center justify-between gap-3 border-b bg-background/95 px-3 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <p className="min-w-0 truncate text-sm font-semibold">
              {eventTwinQuery.data.twin.title} — 3D Conference Twin
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <TwinAvatarCustomizer sceneLabel={eventTwinQuery.data.twin.title} presence={presence} />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-10 gap-1.5"
                aria-label="Exit full screen"
                onClick={() => void exitFullscreen()}
              >
                <Minimize2Icon className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">Exit</span>
              </Button>
            </div>
          </div>
        )}
        <ConferenceTwinScene
          stations={data?.stations ?? []}
          challenges={(data?.challenges ?? []) as PublicChallenge[]}
          onSelect={handleSelect}
          selectedId={selectedKey}
          cameraPreset={currentPreset}
          huntOverlayEnabled={huntOverlayEnabled}
          huntBeacons={huntBeacons}
          virtualScreenContent={virtualScreenContent}
          wallItems={wallItems}
          selectedWallItemId={selectedWallItem?.id}
          onWallItemSelect={handleWallItemSelect}
          ref={cameraRef}
        >
          {livePedestrians.length > 0 && (
            <PedestrianSimulation
              pedestrians={livePedestrians}
              bounds={CONFERENCE_WALK_BOUNDS}
              visibleRoles={ALL_PEDESTRIAN_ROLES}
              selectedId={selectedPed?.id}
              onSelect={handleSelectPedestrian}
            />
          )}
          {pedEnabled && (
            <PedestrianSimulation
              pedestrians={pedestriansRef.current}
              bounds={CONFERENCE_WALK_BOUNDS}
              navGraph={CONFERENCE_NAV_GRAPH}
              timeline={timelineSync ? pedestrianTimeline : null}
              timelineSync={timelineSync}
              showPaths={showPaths}
              visibleRoles={visiblePedestrianRoles}
              selectedId={selectedPed?.id}
              onSelect={handleSelectPedestrian}
              speedMultiplier={pedSpeed}
            />
          )}
        </ConferenceTwinScene>
        <ConferenceTwinLegend
          huntOverlayEnabled={huntOverlayEnabled}
          fullScreenLandscape={isFullscreenLandscape}
        />
        <ZoomPanControls
          onZoomIn={() => cameraRef.current?.zoomIn()}
          onZoomOut={() => cameraRef.current?.zoomOut()}
          onPan={(direction) => cameraRef.current?.pan(direction)}
          onReset={() => cameraRef.current?.reset()}
          onToggleFullscreen={() => void toggleFullscreen()}
          isFullscreen={isFullscreen}
          className={
            isFullscreenLandscape
              ? '!top-16 !bottom-auto right-3 sm:top-16'
              : isTimelineMinimized
                ? 'bottom-3 sm:bottom-4'
                : 'bottom-60 sm:bottom-4'
          }
        />

        <PedestrianControls
          enabled={pedEnabled}
          onToggle={setPedEnabled}
          count={pedCount}
          onSpawn={handleSpawnPedestrian}
          onReset={handleResetPedestrians}
          speed={pedSpeed}
          onSpeedChange={setPedSpeed}
          timelineSync={timelineSync}
          onTimelineSyncChange={setTimelineSync}
          showPaths={showPaths}
          onShowPathsChange={setShowPaths}
          visibleRoles={visiblePedestrianRoles}
          onVisibleRolesChange={setVisiblePedestrianRoles}
          className={isFullscreenLandscape ? 'left-3 top-16 sm:left-[13.5rem] sm:top-16' : 'left-3 top-[4.25rem] sm:left-[13.5rem] sm:top-4'}
          mobileClassName={isFullscreenLandscape ? 'top-16 z-30' : undefined}
          mobileSheetClassName={isFullscreenLandscape ? 'max-h-[calc(100dvh-4rem)]' : undefined}
          mobileMode={isMobile}
          selectedPedestrian={selectedPed}
          onDeselectPedestrian={() => setSelectedPed(null)}
        />

        {selectedWallItem && (
          <TwinWallItemDetails
            item={selectedWallItem}
            isMobile={isMobile}
            isFullscreenLandscape={isFullscreenLandscape}
            isSupporting={supportMutation.isPending}
            error={supportMutation.error instanceof Error ? supportMutation.error.message : null}
            onClose={() => setSelectedWallItem(null)}
            onSupport={() => supportMutation.mutate({ eventSlug: huntSlug, itemId: selectedWallItem.id })}
          />
        )}

        {selected && (
          <>
            {!isMobile && (
              <DesktopTwinPanel
                title={selected.sessionTitle || selected.stationName}
                icon={MapPinIcon}
                className={isFullscreenLandscape ? 'right-4 top-16 z-40 max-h-[calc(100%-5rem)] overflow-auto' : 'right-4 top-4 z-40 max-h-[calc(100%-2rem)] overflow-auto'}
                panelClassName="w-80 shadow-xl"
                contentClassName="space-y-3 p-1 text-sm"
                headerActions={(
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 rounded-lg"
                    aria-label="Close selected details"
                    onClick={() => setSelected(null)}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                )}
              >
                <SelectedDetail
                  item={selected}
                  huntSlug={huntSlug}
                  selectedMinutes={timelineMinutes}
                  completedChallengeIds={completedChallengeIds}
                />
              </DesktopTwinPanel>
            )}
            {isMobile && (
              <Sheet open onOpenChange={(open) => { if (!open) setSelected(null); }}>
                <SheetContent
                  side="bottom"
                  className={isFullscreenLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <SheetHeader>
                    <SheetTitle>{selected.sessionTitle || selected.stationName}</SheetTitle>
                    <SheetDescription>Details for the selected {selected.kind === 'booth' ? 'booth' : 'session'}.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-5 space-y-3 text-sm">
                    <SelectedDetail
                      item={selected}
                      huntSlug={huntSlug}
                      selectedMinutes={timelineMinutes}
                      completedChallengeIds={completedChallengeIds}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </>
        )}

        {!isMobile && (
          <DesktopTwinPanel
            title="Conference timeline"
            icon={ClockIcon}
            className={isFullscreenLandscape ? 'bottom-3 left-3 z-30' : 'bottom-3 left-3 z-20'}
            panelClassName="w-[28rem] max-w-[calc(100vw-1.5rem)]"
            contentClassName="p-1"
          >
            <ScheduleTimelineSlider
              compact
              className="min-w-0"
              value={timelineMinutes}
              onChange={setTimelineMinutes}
              markers={schedule.sessionMarkers}
              completedMarkers={completedSessionMarkers}
              counts={schedule.counts}
              liveRoomNames={schedule.liveRooms.map((room) => room.room)}
              completedRoomNames={completedSessionRooms}
            />
          </DesktopTwinPanel>
        )}

        {isMobile && (isTimelineMinimized ? (
          <div
            className="absolute bottom-3 left-3 z-30"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="size-11 rounded-xl border bg-card/95 shadow-lg backdrop-blur transition-transform hover:scale-105"
                  aria-label={`Expand conference timeline, current event time ${formatScheduleTime12(timelineMinutes)}`}
                  aria-expanded={false}
                  onClick={() => setIsTimelineMinimized(false)}
                >
                  <ClockIcon className="size-5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                Expand conference timeline · {formatScheduleTime12(timelineMinutes)}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div
            className={isFullscreenLandscape
              ? 'absolute inset-x-3 bottom-2 z-30 rounded-xl border border-border bg-background/95 px-3 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] shadow-xl backdrop-blur supports-[backdrop-filter]:bg-background/80 touch-manipulation'
              : 'absolute inset-x-3 bottom-3 z-20 rounded-xl border border-border bg-background/95 px-3 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-xl backdrop-blur supports-[backdrop-filter]:bg-background/80 touch-manipulation'}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <ScheduleTimelineSlider
                compact
                className="min-w-0 flex-1"
                value={timelineMinutes}
                onChange={setTimelineMinutes}
                markers={schedule.sessionMarkers}
                completedMarkers={completedSessionMarkers}
                counts={schedule.counts}
                liveRoomNames={schedule.liveRooms.map((room) => room.room)}
                completedRoomNames={completedSessionRooms}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 shrink-0"
                    aria-label="Minimize conference timeline"
                    aria-expanded={true}
                    onClick={() => setIsTimelineMinimized(true)}
                  >
                    <ChevronDownIcon className="size-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8}>Minimize conference timeline</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TwinWallItemDetails({
  item,
  isMobile,
  isFullscreenLandscape,
  isSupporting,
  error,
  onClose,
  onSupport,
}: {
  item: EventTwinItem;
  isMobile: boolean;
  isFullscreenLandscape: boolean;
  isSupporting: boolean;
  error: string | null;
  onClose: () => void;
  onSupport: () => void;
}) {
  const content = (
    <div className="space-y-4">
      {item.photoUrl && (
        <img
          src={item.photoUrl}
          alt={item.title}
          className="aspect-[3/2] w-full rounded-lg border border-border object-cover"
        />
      )}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <Badge variant="secondary">{item.type === 'hunt_capture' ? 'Hunt find' : 'In-event report'}</Badge>
          <span className="text-xs text-muted-foreground">
            {item.supportCount} {item.type === 'hunt_capture'
              ? item.supportCount === 1 ? 'finder' : 'finders'
              : item.supportCount === 1 ? 'supporter' : 'supporters'}
          </span>
        </div>
        {item.description && <p className="text-muted-foreground">{item.description}</p>}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button className="w-full" onClick={onSupport} disabled={isSupporting}>
        <HeartIcon className="mr-2 size-4" aria-hidden="true" />
        {isSupporting ? 'Recording support…' : 'Show support'}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent
          side="bottom"
          className={isFullscreenLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'}
        >
          <SheetHeader>
            <SheetTitle>{item.title}</SheetTitle>
            <SheetDescription>Wall-mounted conference twin item</SheetDescription>
          </SheetHeader>
          <div className="mt-5">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DesktopTwinPanel
      title={item.title}
      icon={MapPinIcon}
      className={isFullscreenLandscape ? 'right-4 top-16 z-40 max-h-[calc(100%-5rem)] overflow-auto' : 'right-4 top-4 z-40 max-h-[calc(100%-2rem)] overflow-auto'}
      panelClassName="w-80 shadow-xl"
      contentClassName="p-1"
      headerActions={(
        <Button variant="ghost" size="icon" className="size-8 shrink-0 rounded-lg" aria-label="Close wall item details" onClick={onClose}>
          <XIcon className="size-4" />
        </Button>
      )}
    >
      {content}
    </DesktopTwinPanel>
  );
}

function boothZoneLabel(booth: BoothDef): string {
  // Zones match floor-plan pads (image-b01fed1e7219): corridor vs upper/lower rows
  if (booth.facing === 'west') return 'West corridor (Expo / Plenary wall)';
  if (booth.facing === 'south') return 'Sponsors Space — north row';
  if (booth.facing === 'north') return 'Sponsors Space — south row';
  return 'Sponsors Space';
}

function SelectedDetail({
  item,
  huntSlug,
  selectedMinutes,
  completedChallengeIds,
}: {
  item: ConferenceSelectedItem;
  huntSlug: string;
  selectedMinutes: number;
  completedChallengeIds: ReadonlySet<string>;
}) {
  const slotLabel = formatSlotLabel(item.startTime, item.endTime);
  const sessionQuizzes = item.challenges.filter((c) => isSessionQuizChallenge(c));
  const otherChallenges = item.challenges.filter((c) => !isSessionQuizChallenge(c));
  const standNum =
    item.stand ?? parseStandNumber(item.stationName) ?? undefined;
  const booth = standNum != null ? BOOTHS.find((b) => b.stand === standNum) : undefined;
  const exhibitorName =
    item.kind === 'booth' ? parseExhibitorName(item.stationName) : null;
  const sponsorLevel = getBoothSponsorLevel(standNum);
  const sponsorColors = booth ? BOOTH_COLORS[booth.category] : null;

  return (
    <>
      <div className="space-y-2">
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Type</span>
          <Badge variant={item.kind === 'registration' ? 'default' : 'secondary'}>
            {item.kind === 'booth'
              ? `Stand ${standNum ?? item.stand ?? '—'}`
              : item.kind === 'room'
                ? 'Session Room'
                : 'Registration'}
          </Badge>
        </div>

        {item.kind === 'booth' && exhibitorName && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Exhibitor</span>
            <span className="font-medium text-right">{exhibitorName}</span>
          </div>
        )}

        {item.kind === 'booth' && booth && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Floor zone</span>
            <span className="text-xs font-medium text-right max-w-[12rem] leading-snug">
              {boothZoneLabel(booth)}
            </span>
          </div>
        )}

        {(item.scheduleStatus || item.roomLabel || slotLabel || item.presenter) && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-2">
            {item.scheduleStatus && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Status</span>
                <Badge
                  variant={scheduleStatusBadgeVariant(item.scheduleStatus)}
                  className="capitalize"
                >
                  {item.scheduleStatus}
                </Badge>
              </div>
            )}
            {item.roomLabel && (
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-muted-foreground">Room</span>
                <span className="text-xs font-medium text-right">{item.roomLabel}</span>
              </div>
            )}
            {slotLabel && (
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <ClockIcon className="h-3 w-3" aria-hidden />
                  Time
                </span>
                <span className="text-xs font-medium text-right">{slotLabel}</span>
              </div>
            )}
            {item.presenter && (
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <UserIcon className="h-3 w-3" aria-hidden />
                  Presenter
                </span>
                <span className="text-xs font-medium text-right">{item.presenter}</span>
              </div>
            )}
            {item.sessionTitle && item.sessionTitle !== item.stationName && (
              <div className="pt-1 border-t border-border/60">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Session
                </p>
                <p className="text-xs font-medium leading-snug mt-0.5">{item.sessionTitle}</p>
              </div>
            )}
          </div>
        )}

        {item.stationPoints > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Station points</span>
            <span className="font-medium">{item.stationPoints}</span>
          </div>
        )}
        {item.kind === 'booth' && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Sponsor level</span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              {sponsorColors && (
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-border"
                  style={{ backgroundColor: sponsorColors.accent }}
                  aria-hidden
                />
              )}
              {sponsorLevel}
            </span>
          </div>
        )}
      </div>

      {sessionQuizzes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Session attendance quiz
          </p>
          {sessionQuizzes.map((ch) => (
            <SessionQuizCard
              key={ch.id}
              challenge={ch}
              completed={completedChallengeIds.has(ch.id)}
              selectedMinutes={selectedMinutes}
              scheduleStatus={item.scheduleStatus}
              huntSlug={huntSlug}
              compact
            />
          ))}
        </div>
      )}

      {otherChallenges.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Other challenges ({otherChallenges.length})
          </p>
          {otherChallenges.map((ch) => {
            const Icon = CHALLENGE_ICONS[ch.type] ?? MapIcon;
            const done = completedChallengeIds.has(ch.id);
            return (
              <div
                key={ch.id}
                className="flex items-start gap-2 rounded-md border border-border p-2"
              >
                <Icon className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium leading-tight">{ch.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {ch.type.replace(/_/g, ' ')} · {ch.points} pts
                    {done ? ' · done' : ''}
                    {ch.presenter ? ` · ${ch.presenter}` : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : sessionQuizzes.length === 0 ? (
        <p className="text-xs text-muted-foreground">No challenges at this station.</p>
      ) : null}

      <div className="flex flex-col gap-2 pt-1">
        {item.stationId && (
          <Button asChild size="sm" className="w-full">
            <Link to={`/hunt/${huntSlug}`}>
              <QrCodeIcon className="mr-1.5 h-3.5 w-3.5" /> Open Hunt
            </Link>
          </Button>
        )}
        {item.challenges.some((c) => String(c.type) === 'cv_scan') && (
          <Button asChild size="sm" variant="outline" className="w-full">
            <Link to="/ar">
              <CameraIcon className="mr-1.5 h-3.5 w-3.5" /> AR Scanner
            </Link>
          </Button>
        )}
        {item.challenges.some((c) => c.type === 'map_find') && (
          <Button asChild size="sm" variant="outline" className="w-full">
            <Link to="/map">
              <MapIcon className="mr-1.5 h-3.5 w-3.5" /> Open Map
            </Link>
          </Button>
        )}
        {item.challenges.some((c) => c.type === 'demo_report') && (
          <Button asChild size="sm" variant="outline" className="w-full">
            <Link to="/report">
              <MapPinIcon className="mr-1.5 h-3.5 w-3.5" /> File a Report
            </Link>
          </Button>
        )}
      </div>
    </>
  );
}

function getBoothSponsorLevel(stand?: number): string {
  if (!stand) return 'Standard';
  const booth = BOOTHS.find((b) => b.stand === stand);
  if (!booth) return 'Standard';
  return booth.category.charAt(0).toUpperCase() + booth.category.slice(1);
}

